import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import apis from '@src/apis';
import {
  PAYMENT_HUB_RENEWAL_TYPE,
  SUBSCRIPTION_PURCHASE_STATUS,
} from '@src/constants/subscription';
import { PAYMENT_METHOD_TYPE } from '@src/constants/payment';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import useFeatureFlags from './useFeatureFlags';
import { useMappingPackage } from './useMappingPackage';

const useAutoRenewPriceAdjustment = () => {
  const { getFeatureValue } = useFeatureFlags();
  const { subscriptionPurchase } = useSelector((state) => state.user);
  const { status } = subscriptionPurchase;
  const { user } = useSelector((state) => state.auth);

  const [usingOrders, setUsingOrders] = useState([]);

  const autoRenewPriceAdjustment = getFeatureValue(
    FEATURE_KEYS.AUTO_RENEW_PRICE_ADJUSTMENT,
    {
      userId: user.id,
      email: user.email,
      phoneNumber: user.phoneNumber,
    },
  );

  // Get using orders
  useEffect(() => {
    const fetchUsingOrders = async () => {
      const data = await apis.orders.getUsingOrders();
      if (data?.status) setUsingOrders(data.result);
    };
    fetchUsingOrders();
  }, []);

  // Check order auto renew
  const usingStudioOrder =
    usingOrders?.find((order) => order?.id === user?.orderId) ||
    usingOrders?.[0];
  const autorenewEnabled = status === SUBSCRIPTION_PURCHASE_STATUS.ACTIVE;
  const isAutoRenewalPackage =
    usingStudioOrder?.renewalType === PAYMENT_HUB_RENEWAL_TYPE.AUTO_RENEW &&
    autorenewEnabled;

  // Check package version
  const { handleGetPackageCodeMapping, packages } = useMappingPackage();
  const packageCode = handleGetPackageCodeMapping({
    level: usingStudioOrder?.packageInfo?.level,
    type: usingStudioOrder?.packageInfo?.type,
    expiresIn: usingStudioOrder?.packageInfo?.expiresIn,
  });
  const packageMapping = packages.find((pkg) => pkg.code === packageCode);
  const isSameVersionPackage =
    packageMapping?.version &&
    usingStudioOrder?.package?.version &&
    packageMapping?.version === usingStudioOrder?.package?.version;

  // Check order buy on app
  const isBuyOnApp = [
    PAYMENT_METHOD_TYPE.APPLE,
    PAYMENT_METHOD_TYPE.GOOGLE,
  ].includes(usingStudioOrder?.paymentMethod?.type);

  const releaseTime = autoRenewPriceAdjustment?.releaseTime;
  const applyNewPriceAfterDays =
    autoRenewPriceAdjustment?.applyNewPriceAfterDays;

  // Check display days
  const popupAfterDays = autoRenewPriceAdjustment?.popupAfterDays;
  // Check if current time has not passed releaseTime + popupAfterDays days
  const isDisplayDays = popupAfterDays
    ? moment().isSameOrBefore(moment(releaseTime).add(popupAfterDays, 'days'))
    : false;

  const canShowPopup =
    isAutoRenewalPackage &&
    !isBuyOnApp &&
    isDisplayDays &&
    !isSameVersionPackage &&
    packages?.length > 0;

  return {
    canShowPopup,
    releaseTime,
    usingStudioOrder,
    applyNewPriceAfterDays,
  };
};

export default useAutoRenewPriceAdjustment;
